/**
 * Position Label Utilities
 *
 * Derives human-readable labels for order/position sides (e.g., "BOS Win")
 * and game descriptions (e.g., "NBA: BOS at NYK") from Kalshi tickers.
 *
 * Shared between OpenOrdersPanel and CombinedOrdersPositionsPanel.
 */

import { parseKalshiNbaMarketTicker, parseKalshiNbaEventTicker } from './kalshiNbaTicker';

/**
 * Derive a human-readable label for a position side.
 * e.g., ticker KXNBA-...-BOS + side "yes" → "BOS Win"
 */
export function derivePositionLabel(
  ticker: string,
  side: 'yes' | 'no',
  outcomeByTicker?: Record<string, { yes?: string; no?: string }>
): string {
  // Check explicit mapping first
  const outcome = outcomeByTicker?.[ticker]?.[side];
  if (outcome) return outcome;

  // Try to parse from Kalshi ticker
  const marketInfo = parseKalshiNbaMarketTicker(ticker);
  if (marketInfo) {
    const eventInfo = parseKalshiNbaEventTicker(marketInfo.eventTicker);
    if (eventInfo) {
      // The market ticker contains the team code for this contract
      // YES on this market = that team wins
      // NO on this market = the other team wins
      const isAwayTeam = marketInfo.teamCode === eventInfo.awayCode;
      const teamCode =
        side === 'yes' ? marketInfo.teamCode : isAwayTeam ? eventInfo.homeCode : eventInfo.awayCode;
      return `${teamCode} Win`;
    }
  }

  return `${side.toUpperCase()}`;
}

/**
 * Derive a game description from a ticker.
 * e.g., "NBA: BOS at NYK" or falls back to title/ticker.
 */
export function deriveGameDescription(ticker: string, title?: string): string {
  // Try to parse from Kalshi ticker
  const marketInfo = parseKalshiNbaMarketTicker(ticker);
  if (marketInfo) {
    const eventInfo = parseKalshiNbaEventTicker(marketInfo.eventTicker);
    if (eventInfo) {
      return `NBA: ${eventInfo.awayCode} at ${eventInfo.homeCode}`;
    }
  }
  // Fall back to title or ticker
  return title || ticker;
}

/**
 * Extract a game date from a ticker string.
 * Tries NBA ticker parsing first, then falls back to NFL-style date regex.
 * Returns ISO-style date string (YYYY-MM-DD) or null.
 */
export function deriveDateFromTicker(ticker: string): string | null {
  // Try NBA ticker parsing first
  const marketInfo = parseKalshiNbaMarketTicker(ticker);
  if (marketInfo) {
    const eventInfo = parseKalshiNbaEventTicker(marketInfo.eventTicker);
    if (eventInfo) {
      return eventInfo.dateYyyyMmDd;
    }
  }

  // Try to extract date from NFL-style tickers (e.g., KXNFL2TD-26FEB08SEANE-...)
  // Format: YY + 3-letter month + DD (e.g., 26FEB08 = Feb 08, 2026)
  const nflDateMatch = ticker.match(
    /(\d{2})(JAN|FEB|MAR|APR|MAY|JUN|JUL|AUG|SEP|OCT|NOV|DEC)(\d{2})/i
  );
  if (nflDateMatch) {
    const [, yy, monthStr, dd] = nflDateMatch;
    const monthMap: Record<string, string> = {
      JAN: '01',
      FEB: '02',
      MAR: '03',
      APR: '04',
      MAY: '05',
      JUN: '06',
      JUL: '07',
      AUG: '08',
      SEP: '09',
      OCT: '10',
      NOV: '11',
      DEC: '12',
    };
    const mm = monthMap[monthStr.toUpperCase()];
    if (mm) {
      return `20${yy}-${mm}-${dd}`;
    }
  }

  return null;
}
